/**
 * PURE derivations for the application-detail v2 page. No db, no `.server`
 * imports, no side effects — every input arrives as plain data so each
 * function is trivially unit-testable. Behaviors are ported from v1
 * (`$propertyId_.applications_.$applicationId.tsx`) EXACTLY, quirks
 * included — see DETAIL-PAGE-V2-PARITY.md for the recorded truth tables.
 */
import { isDecisionLocked } from '#app/utils/landlord-application-actions'
import type {
	DerivedApplicationStatus,
	DownloadAvailability,
	ScreeningKind,
} from './types'

/**
 * Port of v1 `resolveApplicationStatus` (lines 211-222), argument order
 * normalized to (appStatus, aiProcessingStatus). Quirks preserved:
 * - 'completed' requires the AI job to ALSO be completed; otherwise the app
 *   derives to 'incomplete' (never 'processing').
 * - a db status of 'failed' derives to 'incomplete' (never 'failed').
 * The 'processing'/'failed' members of DerivedApplicationStatus are thus
 * never produced here — v1 surfaces those via aiProcessingStatus directly.
 */
export function deriveApplicationStatus(
	appStatus: string,
	aiProcessingStatus: string | null | undefined,
): DerivedApplicationStatus {
	if (appStatus === 'accepted') return 'accepted'
	if (appStatus === 'declined') return 'declined'
	if (appStatus === 'abandoned') return 'abandoned'
	if (appStatus === 'withdrawn') return 'withdrawn'
	if (appStatus === 'completed' && aiProcessingStatus === 'completed')
		return 'completed'
	return 'incomplete'
}

export type ReportActionMode = 'generate' | 'regenerate'

/**
 * Whether to offer the landlord a (re)generate-report action on the detail
 * page, and which verb to show. Mirrors the `/resources/screening-regenerate`
 * guards (DETAIL-V2-DECISION-RULES.md rule 3) so the button never appears when
 * the endpoint would reject it:
 *   - `'regenerate'` — a finished report exists (`derivedStatus === 'completed'`);
 *     re-running REPLACES it (the destructive, danger-toned path).
 *   - `'generate'` — generation ran and FAILED. A failed run derives to
 *     `'incomplete'` ({@link deriveApplicationStatus}), so the only failure
 *     signal is `aiProcessingStatus === 'failed'`; there is no report to
 *     replace, so this is the non-destructive "make the first report" path
 *     that previously left the landlord with no action.
 * Returns `null` (hide) when: no permission, a decision is recorded (final —
 * also covers the accepted-app-with-failed-AI edge), still pending/processing,
 * or the applicant simply has not submitted yet (no `failed` signal).
 */
export function deriveReportAction(input: {
	canRegenerate: boolean
	derivedStatus: DerivedApplicationStatus
	aiProcessingStatus: string | null | undefined
}): ReportActionMode | null {
	if (!input.canRegenerate) return null
	// Decisions are final: never offer a re-run that would wipe the report a
	// decision was made on (rule 3) — guards the accepted/declined + failed-AI
	// combination that derives to a terminal status, not 'completed'.
	if (isDecisionLocked(input.derivedStatus)) return null
	if (input.derivedStatus === 'completed') return 'regenerate'
	if (input.aiProcessingStatus === 'failed') return 'generate'
	return null
}

/**
 * Pure inputs for the download menu/ZIP offering. The loader computes these
 * booleans from data it already has; this function only encodes the rules
 * (v1 menu: lines 7808-7866; credit availability:
 * VerificationSummaryContent.tsx lines 574-581) plus the pre-screening
 * restrictions.
 */
export interface DownloadAvailabilityInput {
	screeningKind: ScreeningKind
	/** Application belongs to a group (has co-applicants/guarantors). */
	isGroup: boolean
	/** A completed family score exists for the group. */
	hasFamilyScore: boolean
	/** TenCheck report data or pre-generated PDF blob exists. */
	hasCreditReport: boolean
	/** AI report produced an income-verification section. */
	hasIncomeVerification: boolean
	/** AI report produced a rent-verification section. */
	hasRentVerification: boolean
}

export function deriveDownloadAvailability(
	input: DownloadAvailabilityInput,
): DownloadAvailability {
	const isPreScreening = input.screeningKind === 'pre_screening'
	return {
		// v1 always offers "Download Report" + "Download All Files" in the menu.
		individualReport: true,
		allFilesZip: true,
		// Pre-screening never ran a credit pull — false by construction.
		creditReport: !isPreScreening && input.hasCreditReport,
		// Family report: full-screening groups with a generated score only.
		familyReport: !isPreScreening && input.isGroup && input.hasFamilyScore,
		incomeVerification: input.hasIncomeVerification,
		rentVerification: input.hasRentVerification,
	}
}

export interface RelationshipLabelMember {
	applicationId: string
	applicationType: 'primary' | 'co_applicant' | 'guarantor'
	/** adults from THIS member's parsed property step (adults[0] = self). */
	adults:
		| Array<{ email?: string; relationship?: string; name?: string }>
		| undefined
	/** The member's ACCOUNT email — second key for the invitation match (the
	 * invite is usually sent to the address the person signs up with). */
	userEmail?: string | null
}

/** A group invitation row carrying the relationship the inviter filled in —
 * the most reliable relation source (2026-07-13): the primary types it when
 * inviting, and the combined page's Add Applicant captures it too. */
export interface RelationshipInvitation {
	/** Linked once the invitee joins — the strongest match. */
	screeningApplicationId: string | null
	invitedEmail: string | null
	relationship: string | null
}

const normalizeKey = (value: string | undefined): string | undefined => {
	const trimmed = value?.trim().toLowerCase()
	return trimmed ? trimmed : undefined
}

/**
 * Port of v1 `applicantRoleMap` (lines 4587-4628). The PRIMARY's property
 * step lists household adults; index 1+ carry email → relationship (e.g.
 * 'Spouse'). Each non-primary, non-guarantor member is matched by their own
 * adults[0] email (case-insensitive) against that map, falling back to
 * 'Co-Applicant'. v1 quirks preserved: no primary member → EMPTY map; primary
 * with NO adults array (undefined — an empty array proceeds) → EMPTY map, so
 * consumers label every member, even the primary, 'Co-Applicant'.
 *
 * v2 IMPROVEMENT over v1 (recorded, parity §14): when the email match misses
 * (the invite email often differs from what the primary typed in the
 * household list), fall back to matching the member's adults[0] NAME against
 * the primary's adults names before giving up — the design's switcher shows
 * real relationships (Spouse / Parent / Child), not a wall of 'Co-Applicant'.
 *
 * v2 IMPROVEMENT 2026-07-13 (client): the INVITATION is consulted FIRST —
 * the inviter records the relationship on the invite row (primary flow AND
 * the combined page's Add Applicant), and once the invitee joins the row
 * links to their application. Resolution order per co-applicant:
 *  ① invitation matched by screeningApplicationId (joined members)
 *  ② invitation matched by invitedEmail ↔ account email / step adults[0]
 *  ③ the primary's household-roster email/name match (v1 port above)
 *  ④ 'Co-Applicant'.
 * Unlike the roster path, ①/② need no primary member and no adults array —
 * so invitations resolve labels even for the v1-quirk EMPTY-map households.
 */
export function deriveRelationshipLabels(
	members: RelationshipLabelMember[],
	invitations: RelationshipInvitation[] = [],
): Map<string, string> {
	const labels = new Map<string, string>()

	const inviteRelationById = new Map<string, string>()
	const inviteRelationByEmail = new Map<string, string>()
	for (const invitation of invitations) {
		const relationship = invitation.relationship?.trim()
		if (!relationship) continue
		if (invitation.screeningApplicationId) {
			inviteRelationById.set(invitation.screeningApplicationId, relationship)
		}
		const email = normalizeKey(invitation.invitedEmail ?? undefined)
		// First invite wins on a re-invited address — rows arrive newest-first
		// callers need not guarantee order; any row carries the same relation
		// in practice, so don't overwrite an id-linked match with a stale one.
		if (email && !inviteRelationByEmail.has(email)) {
			inviteRelationByEmail.set(email, relationship)
		}
	}
	const invitationRelationFor = (
		member: RelationshipLabelMember,
	): string | undefined => {
		const byId = inviteRelationById.get(member.applicationId)
		if (byId) return byId
		const accountEmail = normalizeKey(member.userEmail ?? undefined)
		const stepEmail = normalizeKey(member.adults?.[0]?.email)
		return (
			(accountEmail ? inviteRelationByEmail.get(accountEmail) : undefined) ??
			(stepEmail ? inviteRelationByEmail.get(stepEmail) : undefined)
		)
	}

	const primary = members.find((m) => m.applicationType === 'primary')
	const primaryAdults = primary?.adults
	const relationshipByEmail = new Map<string, string>()
	const relationshipByName = new Map<string, string>()
	for (const adult of primaryAdults?.slice(1) ?? []) {
		if (!adult.relationship) continue
		const email = normalizeKey(adult.email)
		const name = normalizeKey(adult.name)
		if (email) relationshipByEmail.set(email, adult.relationship)
		if (name) relationshipByName.set(name, adult.relationship)
	}

	for (const member of members) {
		if (member.applicationType === 'primary') {
			labels.set(member.applicationId, 'Primary')
		} else if (member.applicationType === 'guarantor') {
			labels.set(member.applicationId, 'Guarantor')
		} else {
			const fromInvitation = invitationRelationFor(member)
			// v1-quirk boundary: the ROSTER path still requires a primary with an
			// adults array (parity §3) — the invitation path deliberately doesn't.
			const ownEmail = normalizeKey(member.adults?.[0]?.email)
			const ownName = normalizeKey(member.adults?.[0]?.name)
			const fromRoster =
				primary && primaryAdults
					? ((ownEmail ? relationshipByEmail.get(ownEmail) : undefined) ??
						(ownName ? relationshipByName.get(ownName) : undefined))
					: undefined
			const relationship = fromInvitation ?? fromRoster
			// v1 quirk kept for the NO-invitation case: without a primary (or a
			// primary without adults) the map used to be EMPTY — meaning even the
			// primary read 'Co-Applicant' downstream. Invitations now fill real
			// labels, but a household with neither source keeps the old shape.
			labels.set(member.applicationId, relationship || 'Co-Applicant')
		}
	}
	// Preserve the v1 EMPTY-map contract when there is truly nothing to say:
	// no primary-with-adults AND no invitation produced any label. Downstream
	// falls back to 'Co-Applicant' for everyone, exactly as before.
	if (
		(!primary || !primaryAdults) &&
		inviteRelationById.size === 0 &&
		inviteRelationByEmail.size === 0
	) {
		return new Map()
	}
	return labels
}

export interface DisplayNameInput {
	applicationType: 'primary' | 'co_applicant' | 'guarantor'
	/** adults from THIS member's parsed property step. */
	adults: Array<{ name?: string; email?: string }> | undefined
	/** The applicant account's email/name/username (user row). */
	userEmail: string | null
	userName: string | null
	username: string | null
}

/**
 * Port of v1's applicant-name resolution (lines 1998-2007 + 2085-2089):
 * primaries read adults[0]; co-applicants/guarantors match their account
 * email against the adults list (case-insensitive), falling back to
 * adults[0]; then adult name → account name → username → 'Unknown'.
 */
export function deriveDisplayName(input: DisplayNameInput): string {
	const resolvedAdult =
		input.applicationType === 'primary'
			? input.adults?.[0]
			: (input.adults?.find(
					(adult) =>
						adult.email &&
						input.userEmail &&
						adult.email.toLowerCase() === input.userEmail.toLowerCase(),
				) ?? input.adults?.[0])

	return resolvedAdult?.name || input.userName || input.username || 'Unknown'
}

// ---------------------------------------------------------------------------
// Early decline (TT-45, decision D4)
// ---------------------------------------------------------------------------

/** Raw pipeline statuses that count as "finished screening" for the early-
 * decline warning: the member has submitted (report generating or done). */
const FINISHED_RAW_STATUSES = new Set(['submitted', 'processing', 'completed'])
const TERMINAL_RAW_STATUSES = new Set([
	'accepted',
	'declined',
	'withdrawn',
	'abandoned',
])

/**
 * TT-45 (client decision D4): Decline is allowed before every member finishes
 * screening, but never silently — this derives the warning line shown before
 * confirm and echoed on the action result. Input is the RAW statuses of the
 * household's APPLICANTS (primary + co-applicants; guarantors follow the
 * household outcome and don't gate the warning). Terminal members are settled
 * — they count toward neither side. Returns null when nothing is unfinished
 * (the normal, post-report decline).
 */
export function deriveEarlyDeclineWarning(
	applicantRawStatuses: string[],
): string | null {
	const active = applicantRawStatuses.filter(
		(status) => !TERMINAL_RAW_STATUSES.has(status),
	)
	const unfinished = active.filter(
		(status) => !FINISHED_RAW_STATUSES.has(status),
	).length
	if (unfinished === 0) return null
	return `${unfinished} of ${active.length} member${active.length === 1 ? '' : 's'} ${
		unfinished === 1 ? 'has' : 'have'
	} not finished screening`
}

// ---------------------------------------------------------------------------
// Guarded household submit (TT-46, decision D9 — proposal §7 table)
// ---------------------------------------------------------------------------

export type HouseholdInviteChoice = 'cancel' | 'keep'

// ---------------------------------------------------------------------------
// Decisions v3 — per-member transition matrix (DECISIONS-V3-PLAN.md §1)
// ---------------------------------------------------------------------------

export type MemberDecisionAction = 'accept' | 'decline' | 'withdraw' | 'abandon'

const ACTION_TARGET: Record<
	MemberDecisionAction,
	'accepted' | 'declined' | 'withdrawn' | 'abandoned'
> = {
	accept: 'accepted',
	decline: 'declined',
	withdraw: 'withdrawn',
	abandon: 'abandoned',
}

export type MemberTransitionVerdict =
	| { ok: true; newStatus: string }
	| { ok: false; reason: string }

/**
 * Decisions v4 (client 2026-07-20): ANY decision is legal on ANY live
 * member — accept/decline/withdraw/abandon no longer wait for a finished
 * report. Rule 5 ("accept requires that member's finished report") is
 * RETIRED; the only refusal left is the same→same no-op, so the engine
 * never writes/audits noise. The dialog warns (not blocks) when a decision
 * lands on an unfinished member — their report will never be generated
 * (regeneration stays locked on decided members, rule 3).
 *
 * There is deliberately NO path back to UNDECIDED (owner 2026-07-17): a
 * decision only ever becomes another decision. Un-deciding would have to
 * reconstruct the screening status the decision overwrote, which is a guess
 * (`submitted` and `processing` are indistinguishable afterwards) — 10% of
 * the value for 90% of the complexity. With rule 5 gone the old trap (a
 * member declined before their report finished could never be accepted)
 * no longer exists — any decided member can flip to any other decision.
 * If restoring to undecided is ever needed, add a `pre_decision_status`
 * column and restore exactly — never re-infer.
 */
export function validateMemberTransition(input: {
	action: MemberDecisionAction
	/** RAW current status (terminal values are spelled the same raw/derived). */
	currentStatus: string
}): MemberTransitionVerdict {
	const { action, currentStatus } = input
	const target = ACTION_TARGET[action]
	if (currentStatus === target) {
		return {
			ok: false,
			reason: `Member is already ${target} — nothing to change.`,
		}
	}
	return { ok: true, newStatus: target }
}

/** One dialog row's inputs to the v3 submit plan. */
export interface DecisionDialogMember {
	id: string
	role: 'applicant' | 'guarantor'
	/** Current status — terminal values are spelled the same raw/derived. */
	currentStatus: string
	/** The row's selected action; 'later' = leave the member as-is. */
	choice: MemberDecisionAction | 'later'
}

/** What the DecisionDialog's per-row controls resolve to (Decisions v3). */
export interface DecisionDialogPlan {
	/** Legal selections to POST as memberChoices. Empty → Submit disabled. */
	changes: Array<{ id: string; action: MemberDecisionAction }>
	/** First illegal selection's matrix reason — Submit disabled while set. */
	blockedReason: string | null
	/** Any change touches a DECIDED member — informational only (owner
	 * 2026-07-18: the note is OPTIONAL; this no longer gates Submit). The
	 * dialog uses it for the Decision-Saved summary copy. */
	changesDecision: boolean
	/** This submit decides the LAST active applicant — the server cancels the
	 * household's live invitations atomically with it (v3 §3). */
	closesHousehold: boolean
	/** Intent to POST — labels the result; the server applies memberChoices
	 * per member regardless. Precedence: accept > decline > withdraw >
	 * abandon. null = nothing to submit. */
	postIntent: 'approve' | 'decline' | 'withdraw' | 'abandon' | null
	/** Footer counts by selected action. */
	counts: {
		accepted: number
		declined: number
		withdrawn: number
		abandoned: number
	}
}

const DECIDED_STATUSES = new Set([
	'accepted',
	'declined',
	'withdrawn',
	'abandoned',
])

/**
 * Pure submit plan for the v3 DecisionDialog: every row carries its own
 * action (accept/decline/withdraw/abandon/later), validated with the
 * SAME validateMemberTransition matrix the server enforces — the dialog can
 * only ever post a combination the server will save. Mixed outcomes are
 * legal; illegal selections surface the server's exact refusal reason.
 */
export function deriveDecisionDialogPlan(
	members: DecisionDialogMember[],
): DecisionDialogPlan {
	const changes: DecisionDialogPlan['changes'] = []
	let blockedReason: string | null = null
	const counts = {
		accepted: 0,
		declined: 0,
		withdrawn: 0,
		abandoned: 0,
	}
	for (const member of members) {
		if (member.choice === 'later') continue
		const verdict = validateMemberTransition({
			action: member.choice,
			currentStatus: member.currentStatus,
		})
		if (!verdict.ok) {
			blockedReason = blockedReason ?? verdict.reason
			continue
		}
		changes.push({ id: member.id, action: member.choice })
		if (member.choice === 'accept') counts.accepted++
		else if (member.choice === 'decline') counts.declined++
		else if (member.choice === 'withdraw') counts.withdrawn++
		else counts.abandoned++
	}

	const changed = new Map(changes.map((c) => [c.id, c.action]))
	const changesDecision = members.some(
		(m) => changed.has(m.id) && DECIDED_STATUSES.has(m.currentStatus),
	)

	// Post-submit applicant liveness — mirrors the server's invite rule.
	const applicants = members.filter((m) => m.role === 'applicant')
	const closesHousehold =
		applicants.length > 0 &&
		applicants.every((m) => {
			// Every action is a decision now — there is no path back to
			// undecided (owner 2026-07-17).
			if (changed.has(m.id)) return true
			return DECIDED_STATUSES.has(m.currentStatus)
		})

	const postIntent = blockedReason
		? null
		: counts.accepted > 0
			? 'approve'
			: counts.declined > 0
				? 'decline'
				: counts.withdrawn > 0
					? 'withdraw'
					: counts.abandoned > 0
						? 'abandon'
						: null

	return {
		changes,
		blockedReason,
		changesDecision,
		closesHousehold,
		postIntent,
		counts,
	}
}

const ACTION_TO_STATUS: Record<MemberDecisionAction, string> = {
	accept: 'accepted',
	decline: 'declined',
	withdraw: 'withdrawn',
	abandon: 'abandoned',
}

/**
 * Footer tally for the decision console AND the Decision Saved receipt
 * (client 2026-07-21; replaces the changes-only summary): the WHOLE
 * household's resulting states. A row's resulting state is its selected
 * action; an untouched row keeps its recorded decision, or reads Pending
 * while undecided. Accepted · Pending · Declined always render — zeros
 * included ("0 Accepted · 1 Pending · 2 Declined") — so the landlord sees
 * every member accounted for; Withdrawn / Abandoned append only when
 * non-zero. Cancelled invites are deliberately NOT tallied here (client
 * 2026-07-21): the invite rows carry their own "Invite Cancelled" chip,
 * and repeating it in the footer read as a duplicate.
 */
export function summarizeHouseholdTally(
	members: ReadonlyArray<
		Pick<DecisionDialogMember, 'currentStatus' | 'choice'>
	>,
): string {
	const counts = {
		accepted: 0,
		pending: 0,
		declined: 0,
		withdrawn: 0,
		abandoned: 0,
	}
	for (const member of members) {
		const resulting =
			member.choice !== 'later'
				? ACTION_TO_STATUS[member.choice]
				: DECIDED_STATUSES.has(member.currentStatus)
					? member.currentStatus
					: 'pending'
		counts[resulting as keyof typeof counts] += 1
	}
	const parts = [
		`${counts.accepted} Accepted`,
		`${counts.pending} Pending`,
		`${counts.declined} Declined`,
		counts.withdrawn ? `${counts.withdrawn} Withdrawn` : null,
		counts.abandoned ? `${counts.abandoned} Abandoned` : null,
	].filter(Boolean)
	return parts.join(' · ')
}

/**
 * Default pill state for a dialog row when the console opens (Decisions
 * v4, client 2026-07-20 — supersedes the 2026-07-17 entry-only rule):
 *
 * - COMBINED entry (`seedAllUndecided`): EVERY undecided member
 *   pre-selects the opening intent — applicants AND guarantors; there is
 *   no report gate anymore.
 * - INDIVIDUAL entry: only the ENTRY member — the applicant whose page
 *   the landlord clicked from — pre-selects; every other undecided row
 *   opens at 'later', shown as the selected PENDING pill.
 *
 * DECIDED members never take the clicked default on either page — their
 * row displays the recorded decision as the selected past-tense pill and
 * changes only by a deliberate flip ('later' here; the pill selection is
 * derived from the recorded status).
 */
export function deriveDefaultMemberChoice(input: {
	/** Combined-console seeding: every undecided row takes the intent. */
	seedAllUndecided: boolean
	/** The row is the application the console was opened from. */
	isEntry: boolean
	/** RAW current status (terminal values spelled the same raw/derived). */
	currentStatus: string
	intent: 'approve' | 'decline' | 'withdraw' | 'abandon'
}): MemberDecisionAction | 'later' {
	// Withdraw/abandon dialogs are plain confirms — no pills to default.
	if (input.intent !== 'approve' && input.intent !== 'decline') return 'later'
	// Decided rows show their recorded state instead of a seeded change; the
	// matrix would refuse a same→same post anyway.
	if (DECIDED_STATUSES.has(input.currentStatus)) return 'later'
	if (!input.seedAllUndecided && !input.isEntry) return 'later'
	return input.intent === 'approve' ? 'accept' : 'decline'
}
